import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PiArrowLeftBold, PiFilePdfBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { siteConfig } from '@v2/config/site';
import DocContent from '@v2/features/company-docs/DocContent';
import type { CompanyDocument } from '@v2/types/intranet';

/**
 * Versão de impressão do documento: o navegador gera o PDF ("Salvar como PDF") com emoji,
 * diagramas SVG e imagens fiéis — o dompdf do servidor não desenha emoji nem esses SVGs.
 */
export default function DocumentPrintPage() {
    const { id } = useParams<{ id: string }>();
    const [params] = useSearchParams();
    const printed = useRef(false);
    const { data: doc, isError } = useQuery({
        queryKey: ['intranet', 'documents', id, 'print'],
        queryFn: () => api.get<{ data: CompanyDocument }>(`/intranet/documents/${id}`).then((r) => r.data.data),
    });
    const toc = useMemo(() => {
        if (!doc?.content_html) return [];
        const d = new DOMParser().parseFromString(doc.content_html, 'text/html');
        return Array.from(d.querySelectorAll('h2')).map((h) => h.textContent?.trim() ?? '');
    }, [doc?.content_html]);
    const generatedAt = new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    const userName = window.__V2__?.user?.name ?? '';

    useEffect(() => {
        if (doc) document.title = `${doc.title} — v${doc.current_version ?? 0}`;
    }, [doc]);

    // Abre a caixa de impressão sozinho quando as imagens terminam de carregar.
    useEffect(() => {
        if (!doc || printed.current || params.get('auto') === '0') return;
        const images = Array.from(document.querySelectorAll<HTMLImageElement>('.print-doc img'));
        Promise.all(images.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => { img.onload = img.onerror = () => res(null); }))))
            .then(() => window.setTimeout(() => {
                printed.current = true;
                window.print();
            }, 300));
    }, [doc, params]);

    if (isError) {
        return <p className="p-10 text-center text-sm text-gray-500">Documento não encontrado ou sem acesso.</p>;
    }
    if (!doc) {
        return <p className="p-10 text-center text-sm text-gray-400">Preparando o PDF…</p>;
    }

    return (
        <div className="print-root min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
            <style>{`
                @page { size: A4; margin: 16mm 14mm 20mm; }
                @media print {
                    .no-print { display: none !important; }
                    .print-doc { box-shadow: none !important; border: 0 !important; width: auto !important; padding: 0 !important; margin: 0 !important; }
                    .print-doc h2, .print-doc h3 { break-after: avoid; }
                    .print-doc img, .print-doc table, .print-doc blockquote, .print-doc li { break-inside: avoid; }
                    .print-footer { position: fixed; bottom: 0; left: 0; right: 0; }
                    a { color: inherit !important; text-decoration: none !important; }
                }
                .print-doc .doc-content { font-size: 10.5pt; color: #1f2937; }
                .print-doc .doc-content h2 { font-size: 15pt; }
                .print-doc .doc-content h3 { font-size: 12pt; }
                .print-doc .doc-content img { max-height: 120mm; width: auto; }
                .print-doc .doc-content table { display: table; }
            `}</style>

            <div className="no-print mx-auto mb-4 flex w-[210mm] max-w-full items-center justify-between gap-2 px-2">
                <Link to={`/company/documents/${doc.id}`} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
                    <PiArrowLeftBold className="size-4" /> Voltar ao documento
                </Link>
                <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-700"
                >
                    <PiFilePdfBold className="size-4" /> Salvar como PDF / imprimir
                </button>
            </div>

            <article className="print-doc mx-auto w-[210mm] max-w-full bg-white px-[14mm] py-[14mm] shadow-lg" data-theme="light">
                <header className="mb-6 border-b-2 border-gray-900 pb-4">
                    <div className="flex items-start justify-between gap-4">
                        <img src={siteConfig.logo} alt="Buckler" className="h-8" />
                        <div className="text-right text-[9pt] leading-tight text-gray-500">
                            <p className="font-semibold uppercase tracking-wide text-gray-700">{doc.category_label}</p>
                            <p>Versão {doc.current_version ?? 0}</p>
                            <p>Atualizado em {doc.updated_at_label}</p>
                        </div>
                    </div>
                    <h1 className="mt-5 text-[20pt] font-bold leading-tight text-gray-900">{doc.title}</h1>
                    {doc.description && <p className="mt-1 text-[10.5pt] text-gray-600">{doc.description}</p>}
                    <dl className="mt-3 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 px-3 py-2 text-[8.5pt] text-gray-600">
                        <div>
                            <dt className="font-semibold uppercase tracking-wide text-gray-400">Última alteração</dt>
                            <dd>{doc.updated_by ?? '—'}</dd>
                        </div>
                        <div>
                            <dt className="font-semibold uppercase tracking-wide text-gray-400">Quem pode ver</dt>
                            <dd>{doc.access?.view?.join(', ') || 'Todos os usuários do Hub'}</dd>
                        </div>
                        <div>
                            <dt className="font-semibold uppercase tracking-wide text-gray-400">Aprovação</dt>
                            <dd>{doc.requires_approval ? `Sim — ${(doc.access?.approve ?? []).join(', ')}` : 'Não precisa'}</dd>
                        </div>
                    </dl>
                </header>

                {toc.length > 2 && (
                    <nav className="mb-6 rounded-lg border border-gray-200 px-4 py-3">
                        <p className="mb-1 text-[8.5pt] font-semibold uppercase tracking-wide text-gray-400">Sumário</p>
                        <ul className="space-y-0.5 text-[10pt] text-gray-700">
                            {toc.map((t, i) => (
                                <li key={i}>{t}</li>
                            ))}
                        </ul>
                    </nav>
                )}

                <DocContent html={doc.content_html} markdown={doc.content_markdown} />

                {(doc.feature_links ?? []).length > 0 && (
                    <section className="mt-8 border-t border-gray-200 pt-3 text-[9pt] text-gray-600">
                        <p className="font-semibold text-gray-800">Telas do Hub citadas</p>
                        <p>{doc.feature_links!.map((l) => `${l.label} (${window.location.origin}${l.path})`).join(' · ')}</p>
                    </section>
                )}

                <footer className="print-footer mt-8 border-t border-gray-200 bg-white px-[14mm] pt-2 text-[7.5pt] text-gray-400 print:px-[14mm]">
                    Buckler · {doc.title} · v{doc.current_version ?? 0} · gerado em {generatedAt}
                    {userName ? ` por ${userName}` : ''} · Cópia impressa: a versão válida é sempre a do Hub.
                </footer>
            </article>
        </div>
    );
}
